/**
 * Task #4257 — etichette condivise del catalogo permessi (categorie e portate).
 */
import type { PermissionGrantKind, PermissionGrantScopeLevel } from '@easyflow/shared';

export const GRANT_KIND_LABELS: Record<PermissionGrantKind, string> = {
  potere: 'Potere aggiuntivo',
  allargamento: 'Allargamento del recinto',
};

/**
 * Domini canonici del catalogo permessi, nell'ordine in cui vanno mostrati.
 * Le categorie grezze in DB sono miste (`hr`/`HR`, `pos`/`POS`, `ai`,
 * `live_coaching`, chiavi italiane maiuscole…): ogni consumer passa da
 * `normalizePermissionCategory` così lo stesso dominio non compare due volte
 * e nessuna etichetta arriva grezza in UI. Il seed non si tocca.
 */
export const PERMISSION_DOMAIN_KEYS = [
  'admin', 'utenti', 'ruoli', 'settings', 'general',
  'voip', 'estensioni', 'trunk', 'ivr', 'code', 'cdr', 'coaching', 'analytics', 'dashboard', 'logs',
  'crm', 'calendar', 'tasks', 'messenger', 'comunicazione', 'video_call',
  'hr', 'incentivazione',
  'pos', 'magazzino',
  'ai_tools', 'file_manager', 'site_builder', 'integrations', 'support',
] as const;
export type PermissionDomainKey = (typeof PERMISSION_DOMAIN_KEYS)[number];

export const PERMISSION_DOMAIN_LABELS: Record<PermissionDomainKey, string> = {
  admin: 'Amministrazione',
  utenti: 'Utenti',
  ruoli: 'Ruoli',
  settings: 'Impostazioni',
  general: 'Generale',
  voip: 'VoIP & Telefonia',
  estensioni: 'Estensioni',
  trunk: 'Trunk',
  ivr: 'IVR',
  code: 'Code',
  cdr: 'CDR',
  coaching: 'Coaching',
  analytics: 'Analytics',
  dashboard: 'Dashboard',
  logs: 'Log & Diagnostica',
  crm: 'CRM',
  calendar: 'Calendario',
  tasks: 'Attività',
  messenger: 'Messenger',
  comunicazione: 'Comunicazione',
  video_call: 'Video Call',
  hr: 'Risorse Umane',
  incentivazione: 'Incentivazione',
  pos: 'Punto Cassa',
  magazzino: 'Magazzino',
  ai_tools: 'Strumenti AI',
  file_manager: 'File Manager',
  site_builder: 'Site Builder',
  integrations: 'Integrazioni',
  support: 'Supporto',
};

/** Categorie grezze (già in minuscolo) che non coincidono con la chiave canonica. */
const PERMISSION_CATEGORY_ALIASES: Record<string, PermissionDomainKey> = {
  ai: 'ai_tools',
  live_coaching: 'coaching',
  impostazioni: 'settings',
};

const DOMAIN_ORDER = new Map<string, number>(PERMISSION_DOMAIN_KEYS.map((key, index) => [key, index]));

function isDomainKey(value: string): value is PermissionDomainKey {
  return DOMAIN_ORDER.has(value);
}

export interface PermissionDomain {
  /** Chiave canonica (dominio noto) o categoria grezza normalizzata se sconosciuta. */
  key: string;
  label: string;
  /** Posizione di visualizzazione; i domini sconosciuti vanno in coda, in ordine alfabetico. */
  order: number;
}

export function normalizePermissionCategory(category: string): PermissionDomain {
  const raw = category.trim().toLocaleLowerCase('it').replace(/[\s-]+/g, '_');
  const key = PERMISSION_CATEGORY_ALIASES[raw] ?? raw;
  if (isDomainKey(key)) {
    return { key, label: PERMISSION_DOMAIN_LABELS[key], order: DOMAIN_ORDER.get(key) ?? 0 };
  }
  return { key: raw || 'altro', label: category.trim() || 'Altro', order: PERMISSION_DOMAIN_KEYS.length };
}

export function permissionCategoryLabel(category: string): string {
  return normalizePermissionCategory(category).label;
}

export function comparePermissionDomains(a: PermissionDomain, b: PermissionDomain): number {
  return a.order - b.order || a.label.localeCompare(b.label, 'it');
}

export interface PermissionDomainGroup<P extends { category: string; name: string }> extends PermissionDomain {
  permissions: P[];
}

/**
 * Raggruppa la lista piatta per dominio canonico (unendo le varianti della
 * stessa categoria) e ordina domini e permessi in modo stabile.
 */
export function groupPermissionsByDomain<P extends { category: string; name: string }>(permissions: readonly P[]): PermissionDomainGroup<P>[] {
  const groups = new Map<string, PermissionDomainGroup<P>>();
  for (const permission of permissions) {
    const domain = normalizePermissionCategory(permission.category);
    const group = groups.get(domain.key);
    if (group) group.permissions.push(permission);
    else groups.set(domain.key, { ...domain, permissions: [permission] });
  }
  return [...groups.values()]
    .sort(comparePermissionDomains)
    .map((group) => ({ ...group, permissions: [...group.permissions].sort((a, b) => a.name.localeCompare(b.name, 'it')) }));
}

/** Stessa suddivisione, nella forma `Record<chiave dominio, permessi>` attesa dai consumer esistenti. */
export function groupPermissionsRecord<P extends { category: string; name: string }>(permissions: readonly P[]): Record<string, P[]> {
  return Object.fromEntries(groupPermissionsByDomain(permissions).map((group) => [group.key, group.permissions]));
}

/** Portata di una concessione per utente. */
export const GRANT_SCOPE_LABELS: Record<PermissionGrantScopeLevel, string> = {
  base: 'Recinto effettivo',
  stores: 'Sedi elencate',
  company: 'Ragione sociale',
  tenant: 'Tutto il tenant',
};
